/**
 * A client's one wallet and its SMS credits (`docs/28_UNIFIED_BILLING.md`).
 *
 * The wallet is the money every service is paid from — AI, email, RCS, and
 * SMS credits bought with it. Here staff top it up (once however often the
 * button is pressed), set when the client is told it is low, and read every
 * movement with the service it was for and, for staff alone, what the vendor
 * charged.
 *
 * SMS credits are a count, not money: what the client may buy them at (a
 * price per credit with a least and a most, and packages), how much it may
 * buy without anybody here looking, which SMS connections are sold in credits,
 * and the one way credits change by hand — an adjustment with a reason, which
 * alone may return taken credits to the wallet as money.
 *
 * Amounts are the server's strings, formatted and never computed with.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Plus, Wallet } from 'lucide-react';
import {
  ApiError,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Modal,
  QueryPending,
  ReadOnly,
  Select,
  StatusChip,
  Switch,
  api,
  formatDateTime,
  formatMoney,
  formatNumber,
  toast,
  useCan,
  useIdempotencyKey,
} from '@connect/web-shared';

import { refusalText } from './api-refusal';
import { entryWords, formatMetered } from './ai-card';

export type WalletService = 'wallet' | 'ai' | 'email' | 'rcs' | 'sms' | 'push';

export interface BillingWalletEntry {
  public_id: string;
  service: WalletService;
  kind: string;
  amount: string;
  balance_after: string;
  description: string;
  provider_cost: string | null;
  /** Staff only: the model that answered an AI call — never in the client's words. */
  usage?: { model?: string | null };
  at: string;
}

export interface CreditPlan {
  on_sale: boolean;
  /** One way of pricing: any quantity at a price per credit, or only packages. */
  pricing: 'fixed' | 'packages';
  price_per_credit: string | null;
  min_quantity: number | null;
  max_quantity: number | null;
  packages: { id: string; label: string; credits: number; price: string }[];
  auto_approve_limit: string | null;
  provider_cost_per_credit: string | null;
}

export interface StaffPurchase {
  public_id: string;
  quantity: number;
  amount: string;
  package: string | null;
  status: 'processing' | 'added' | 'not_completed';
  state: 'awaiting_provider' | 'provider_loaded' | 'unconfirmed' | 'allocated' | 'released';
  route: 'instant' | 'first_purchase' | 'over_limit' | 'needs_load';
  provider_cost: string | null;
  balance_before: string | null;
  balance_before_at: string | null;
  balance_after: string | null;
  provider_reference: string | null;
  note: string | null;
  loaded_at: string | null;
  at: string;
  finished_at: string | null;
  client: { public_id: string; name: string } | null;
}

export interface BillingClient {
  public_id: string;
  name: string;
  wallet: {
    balance: string;
    currency: string;
    low_balance_threshold: string | null;
    low: boolean;
    spent_30_days: Partial<Record<WalletService, string>>;
    entries: BillingWalletEntry[];
    has_more: boolean;
  };
  sms: {
    balance: number;
    reserved: number;
    low_balance_threshold: number | null;
    low: boolean;
    used_30_days: number;
    plan: CreditPlan | null;
    headroom: number | null;
    purchases: StaffPurchase[];
  };
  sms_connections: {
    public_id: string;
    label: string | null;
    provider: string;
    is_primary: boolean;
    billing_mode: 'client_pays_vendor' | 'credits';
    vendor_balance: string | null;
    vendor_balance_at: string | null;
  }[];
  /** What a new plan starts from (Billing → Rules). */
  credit_defaults?: { price_per_credit: string | null; provider_cost_per_credit: string | null };
}

export const billingKeys = {
  all: ['billing'] as const,
  client: (id: string) => ['billing', 'client', id] as const,
  purchases: (state: string) => ['billing', 'purchases', state] as const,
  purchase: (id: string) => ['billing', 'purchase', id] as const,
};

export const SERVICE_LABELS: Record<WalletService, string> = {
  wallet: 'Wallet',
  ai: 'AI',
  email: 'Email',
  rcs: 'RCS',
  sms: 'SMS credits',
  push: 'Push',
};

export const STATE_LABELS = {
  awaiting_provider: 'Waiting on Connect',
  provider_loaded: 'Load not seen yet',
  unconfirmed: 'Needs a decision',
  allocated: 'Added',
  released: 'Money returned',
};

export const STATE_TONES = {
  awaiting_provider: 'warning',
  provider_loaded: 'info',
  unconfirmed: 'danger',
  allocated: 'success',
  released: 'neutral',
} as const;

const KIND_LABELS = {
  top_up: 'Top-up',
  refund: 'Refund',
  adjustment: 'Adjustment',
  charge: 'Charge',
  hold: 'Held',
  release: 'Returned',
  purchase: 'Purchase',
  inbound: 'Incoming email',
};

export function BillingCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.billing.edit');
  const canTopUp = useCan('messaging.billing.create');
  const canAdjust = useCan('messaging.credit_adjustment.create');
  const url = `/billing/clients/${clientPublicId}`;
  const key = billingKeys.client(clientPublicId);
  const billing = useQuery({ queryKey: key, queryFn: () => api.get<BillingClient>(url) });
  const [adding, setAdding] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [threshold, setThreshold] = useState<string | null>(null);
  const [creditAlert, setCreditAlert] = useState<string | null>(null);

  const settle = (data: BillingClient, message = 'Saved') => {
    queryClient.setQueryData(key, data);
    toast.success(message);
  };
  const failed = (e: unknown) => toast.error('Could not change it', e instanceof ApiError ? e.message : undefined);
  const setWallet = useMutation({
    mutationFn: (value: string | null) => api.patch<BillingClient>(`${url}/wallet`, { low_balance_threshold: value }),
    onSuccess: (data) => settle(data),
    onError: failed,
  });
  const setCredits = useMutation({
    mutationFn: (value: number | null) =>
      api.patch<BillingClient>(`${url}/credits/sms`, { low_balance_threshold: value }),
    onSuccess: (data) => settle(data),
    onError: failed,
  });
  // Credits made what the vendor balance pays for, read from the vendor now.
  const matchVendor = useMutation({
    mutationFn: () => api.post<BillingClient>(`${url}/credits/sms/match-vendor`, {}),
    onSuccess: (data) => settle(data, `${clientName}: ${formatNumber(data.sms.balance)} SMS credits, as the vendor balance pays for`),
    onError: failed,
  });
  const setMode = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: 'client_pays_vendor' | 'credits' }) =>
      api.put<BillingClient>(`/billing/connections/${id}/mode`, { mode }),
    onSuccess: (data) => settle(data),
    onError: failed,
  });

  if (billing.data === undefined) return <QueryPending query={billing} label="Loading the wallet" />;
  const data = billing.data;
  const wallet = data.wallet;
  const sms = data.sms;
  const spent = Object.entries(wallet.spent_30_days) as [WalletService, string][];
  const sellsCredits = data.sms_connections.some((c) => c.billing_mode === 'credits');

  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-3">
        <Card>
          <CardHeader
            title={
              <span className="inline-flex items-center gap-2">
                <Wallet className="size-4" />
                Wallet
              </span>
            }
            description={`${clientName}'s one balance: AI, email, RCS and SMS credits are all paid from it.`}
            actions={
              canTopUp ? (
                <Button size="sm" onClick={() => setAdding(true)}>
                  <Plus className="size-3.5" />
                  Top up or adjust
                </Button>
              ) : null
            }
          />
          <CardBody className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <p className="text-2xs uppercase tracking-wide text-muted">Balance</p>
                <p className={wallet.low ? 'text-lg font-semibold text-warning' : 'text-lg font-semibold text-heading'}>
                  {formatMoney(wallet.balance, wallet.currency)}
                </p>
              </div>
              <div>
                <p className="text-2xs uppercase tracking-wide text-muted">30 days, by service</p>
                {spent.length === 0 ? (
                  <p className="text-sm text-muted">Nothing yet</p>
                ) : (
                  <p className="text-sm text-body">
                    {spent.map(([service, amount]) => `${SERVICE_LABELS[service]} ${formatMoney(amount)}`).join(' · ')}
                  </p>
                )}
              </div>
              <div className="flex items-end gap-2">
                <Input
                  size="sm"
                  label="Tell them below (₹)"
                  inputMode="decimal"
                  placeholder="No alert"
                  value={threshold ?? wallet.low_balance_threshold ?? ''}
                  onChange={(e) => setThreshold(e.target.value)}
                />
                {canEdit && threshold !== null ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={setWallet.isPending}
                    onClick={() => setWallet.mutate(threshold.trim() || null, { onSuccess: () => setThreshold(null) })}
                  >
                    Save
                  </Button>
                ) : null}
              </div>
            </div>
            {wallet.entries.length === 0 ? (
              <p className="text-sm text-muted">No movements yet.</p>
            ) : (
              <table className="w-full text-xs">
                <tbody>
                  {wallet.entries.slice(0, 12).map((entry) => (
                    <tr key={entry.public_id} className="border-t border-line">
                      <td className="py-1.5 text-muted">{formatDateTime(entry.at)}</td>
                      <td className="py-1.5">{SERVICE_LABELS[entry.service]}</td>
                      <td className="py-1.5">
                        <StatusChip value={entry.kind} labels={KIND_LABELS} />
                      </td>
                      <td className="max-w-xs truncate py-1.5 text-muted">{entryWords(entry)}</td>
                      <td className="py-1.5 text-right font-mono tabular">{formatMetered(entry.amount)}</td>
                      <td className="py-1.5 text-right font-mono tabular text-muted">
                        {entry.provider_cost ? `cost ${formatMetered(entry.provider_cost)}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
  
        {data.sms_connections.length > 0 ? (
          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <MessageSquare className="size-4" />
                  SMS credits
                </span>
              }
              description="One credit is one SMS part. A connection sold in credits sends on an account IngeniousPro holds; one where the client pays the vendor is billed by the vendor."
              actions={
                <div className="flex gap-2">
                  {canEdit ? (
                    <Button size="sm" variant="secondary" onClick={() => setPlanOpen(true)}>
                      Prices
                    </Button>
                  ) : null}
                  {canAdjust && sellsCredits ? (
                    <Button size="sm" variant="secondary" onClick={() => setAdjusting(true)}>
                      Adjust credits
                    </Button>
                  ) : null}
                  {canAdjust && sellsCredits ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={matchVendor.isPending}
                      title="Read the vendor balance now and set the credits to what it pays for, at the vendor's cost a credit"
                      onClick={() => matchVendor.mutate()}
                    >
                      Match vendor balance
                    </Button>
                  ) : null}
                </div>
              }
            />
            <CardBody className="space-y-3">
              <ul className="space-y-2">
                {data.sms_connections.map((connection) => (
                  <li
                    key={connection.public_id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-line p-2"
                  >
                    <div className="min-w-0 text-sm">
                      <p className="font-medium text-heading">
                        {connection.label ?? 'SMS'}
                        {connection.is_primary ? <span className="text-xs text-muted"> · primary</span> : null}
                      </p>
                      <p className="text-xs text-muted">
                        {connection.vendor_balance
                          ? `Vendor balance ${formatMetered(connection.vendor_balance)}, read ${formatDateTime(connection.vendor_balance_at ?? '')}`
                          : 'Vendor balance not read yet'}
                      </p>
                    </div>
                    <Switch
                      label={connection.billing_mode === 'credits' ? 'Sold in credits' : 'Client pays the vendor'}
                      checked={connection.billing_mode === 'credits'} disabled={setMode.isPending}
                      onChange={(e) =>
                        setMode.mutate({
                          id: connection.public_id,
                          mode: e.target.checked ? 'credits' : 'client_pays_vendor',
                        })
                      }
                    />
                  </li>
                ))}
              </ul>
              {sellsCredits ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <div>
                      <p className="text-2xs uppercase tracking-wide text-muted">Credits left</p>
                      <p className={sms.low ? 'text-lg font-semibold text-warning' : 'text-lg font-semibold text-heading'}>
                        {formatNumber(sms.balance)}
                      </p>
                      {sms.reserved ? <p className="text-xs text-muted">{formatNumber(sms.reserved)} held</p> : null}
                    </div>
                    <div>
                      <p className="text-2xs uppercase tracking-wide text-muted">Used, 30 days</p>
                      <p className="text-sm text-body">{formatNumber(sms.used_30_days)}</p>
                    </div>
                    <div>
                      <p className="text-2xs uppercase tracking-wide text-muted">Room at the vendor</p>
                      <p className={sms.headroom !== null && sms.headroom < 0 ? 'text-sm text-danger' : 'text-sm text-body'}>
                        {sms.headroom === null ? 'Unknown' : `${formatNumber(sms.headroom)} credits`}
                      </p>
                    </div>
                    <div className="flex items-end gap-2">
                      <Input
                        size="sm"
                        label="Tell them below (credits)"
                        inputMode="numeric"
                        placeholder="No alert"
                        value={creditAlert ?? (sms.low_balance_threshold === null ? '' : String(sms.low_balance_threshold))}
                        onChange={(e) => setCreditAlert(e.target.value)}
                      />
                      {canEdit && creditAlert !== null ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={setCredits.isPending}
                          onClick={() =>
                            setCredits.mutate(/^\d+$/.test(creditAlert.trim()) ? Number(creditAlert.trim()) : null, {
                              onSuccess: () => setCreditAlert(null),
                            })
                          }
                        >
                          Save
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-xs text-muted">
                    {sms.plan?.on_sale
                      ? [
                          sms.plan.pricing === 'packages'
                            ? `packages only (${sms.plan.packages.length})`
                            : sms.plan.price_per_credit
                              ? `fixed ${formatMetered(sms.plan.price_per_credit)} a credit`
                              : null,
                          sms.plan.auto_approve_limit
                            ? `added at once up to ${formatMoney(sms.plan.auto_approve_limit)} when the vendor has room`
                            : 'every purchase is looked at',
                          sms.plan.provider_cost_per_credit
                            ? `vendor ${formatMetered(sms.plan.provider_cost_per_credit)} a credit`
                            : 'vendor cost not set',
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      : 'Not on sale: set its prices so the client can buy credits.'}
                  </p>
                  {sms.purchases.length > 0 ? (
                    <table className="w-full text-xs">
                      <tbody>
                        {sms.purchases.slice(0, 8).map((purchase) => (
                          <tr key={purchase.public_id} className="border-t border-line">
                            <td className="py-1.5 text-muted">{formatDateTime(purchase.at)}</td>
                            <td className="py-1.5">{formatNumber(purchase.quantity)} credits</td>
                            <td className="py-1.5 text-right font-mono tabular">{formatMoney(purchase.amount)}</td>
                            <td className="py-1.5 pl-3">
                              <Link className="hover:underline" to={`/messaging/credit-purchases?open=${purchase.public_id}`}>
                                <StatusChip value={purchase.state} labels={STATE_LABELS} tones={STATE_TONES} />
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </>
              ) : null}
            </CardBody>
          </Card>
        ) : null}
  
        {adding ? (
          <WalletEntryDialog<BillingClient>
            url={`${url}/wallet/entries`}
            title={`Wallet — ${clientName}`}
            note="A top-up has a least amount (Billing → Rules). A smaller credit — a trial, a correction — is an adjustment."
            onClose={() => setAdding(false)}
            onDone={(next) => settle(next, `${clientName}'s wallet: ${formatMoney(next.wallet.balance)}`)}
          />
        ) : null}
        {adjusting ? (
          <CreditAdjustmentDialog
            url={`${url}/credits/sms/adjustments`}
            clientName={clientName}
            onClose={() => setAdjusting(false)}
            onDone={(next) => settle(next, `${clientName}: ${formatNumber(next.sms.balance)} SMS credits`)}
          />
        ) : null}
        {planOpen ? (
          <CreditPlanDialog
            url={`${url}/credits/sms/plan`}
            plan={sms.plan}
            defaults={data.credit_defaults}
            onClose={() => setPlanOpen(false)}
            onDone={(next) => settle(next)}
          />
        ) : null}
      </div>
    </ReadOnly>
  );
}

/**
 * A top-up, refund or adjustment of a client's wallet. Made once however
 * often it is pressed: the idempotency key belongs to the entry intended, not
 * to the click. What the server refuses stays in the dialog beside what was
 * typed — a toast alone is gone before the amount has been corrected.
 */
export function WalletEntryDialog<T>({
  url,
  title,
  note,
  onClose,
  onDone,
}: {
  url: string;
  title: string;
  note?: string;
  onClose: () => void;
  onDone: (data: T) => void;
}) {
  const keys = useIdempotencyKey();
  const [kind, setKind] = useState<'top_up' | 'refund' | 'adjustment'>('top_up');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const body = { kind, amount: amount.trim(), description: description.trim() };
  const add = useMutation({
    mutationFn: () => api.post<T>(url, { ...body, idempotency_key: keys.keyFor(body) }),
    onSuccess: (data) => {
      onDone(data);
      onClose();
    },
    onError: (e) => toast.error('Not added', refusalText(e)),
  });
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={title}
      description="Every entry is kept; a mistake is corrected with another entry."
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={add.isPending} disabled={!amount.trim() || !description.trim()} onClick={() => add.mutate()}>
            Add
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Select
          label="Kind"
          value={kind}
          options={[
            { value: 'top_up', label: 'Top-up (payment received)' },
            { value: 'refund', label: 'Refund (credit back)' },
            { value: 'adjustment', label: 'Adjustment (+ or −)' },
          ]}
          onChange={(e) => setKind(e.target.value as typeof kind)}
        />
        <Input
          label="Amount (₹)"
          inputMode="decimal"
          required
          value={amount}
          hint={kind === 'adjustment' ? 'Negative takes away.' : undefined}
          onChange={(e) => setAmount(e.target.value)}
        />
        <Input
          label="Reference"
          required
          maxLength={255}
          placeholder="UTR 3021… / invoice INV-…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {note ? <p className="text-xs text-muted">{note}</p> : null}
        {add.error ? (
          <p role="alert" className="text-xs text-danger">
            {refusalText(add.error) ?? 'The entry could not be added.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/** Credits given or taken by hand, with a reason — and taken credits returned as money only if asked. */
function CreditAdjustmentDialog({
  url,
  clientName,
  onClose,
  onDone,
}: {
  url: string;
  clientName: string;
  onClose: () => void;
  onDone: (data: BillingClient) => void;
}) {
  const keys = useIdempotencyKey();
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState('');
  const valid = /^-?\d+$/.test(quantity.trim()) && Number(quantity.trim()) !== 0 && reason.trim().length >= 3;
  const body = {
    quantity: Number(quantity.trim()),
    reason: reason.trim(),
    ...(refund.trim() ? { refund: refund.trim() } : {}),
  };
  const save = useMutation({
    mutationFn: () => api.post<BillingClient>(url, { ...body, idempotency_key: keys.keyFor(body) }),
    onSuccess: (data) => {
      onDone(data);
      onClose();
    },
    onError: (e) => toast.error('Not changed', refusalText(e)),
  });
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`SMS credits — ${clientName}`}
      description="Never automatic. Each adjustment is kept with its reason and who made it."
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input
          label="Credits"
          inputMode="numeric"
          required
          value={quantity}
          hint="Negative takes credits away."
          onChange={(e) => setQuantity(e.target.value)}
        />
        <Input label="Reason" required maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
        {quantity.trim().startsWith('-') ? (
          <Input
            label="Return to the wallet (₹)"
            inputMode="decimal"
            value={refund}
            hint="Only if the credits taken are to become money again. Leave empty otherwise."
            onChange={(e) => setRefund(e.target.value)}
          />
        ) : null}
        {save.error ? (
          <p role="alert" className="text-xs text-danger">
            {refusalText(save.error) ?? 'The credits could not be changed.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/** What the client pays for its SMS credits, and how much it may buy unattended. */
function CreditPlanDialog({
  url,
  plan,
  defaults,
  onClose,
  onDone,
}: {
  url: string;
  plan: CreditPlan | null;
  defaults?: BillingClient['credit_defaults'];
  onClose: () => void;
  onDone: (data: BillingClient) => void;
}) {
  // A client with no plan yet starts from the installation's default — a
  // fixed price, the vendor's own rate — never a figure written here.
  const [pricing, setPricing] = useState<'fixed' | 'packages'>(plan?.pricing ?? 'fixed');
  const [price, setPrice] = useState(plan ? (plan.price_per_credit ?? '') : (defaults?.price_per_credit ?? ''));
  const [least, setLeast] = useState(plan?.min_quantity ? String(plan.min_quantity) : '');
  const [most, setMost] = useState(plan?.max_quantity ? String(plan.max_quantity) : '');
  const [limit, setLimit] = useState(plan?.auto_approve_limit ?? '');
  const [cost, setCost] = useState(
    plan ? (plan.provider_cost_per_credit ?? '') : (defaults?.provider_cost_per_credit ?? ''),
  );
  const [packages, setPackages] = useState(
    (plan?.packages ?? []).map((p) => ({ id: p.id, label: p.label, credits: String(p.credits), price: p.price })),
  );
  const whole = (value: string) => (value.trim() ? Number(value.trim()) : null);
  const save = useMutation({
    mutationFn: () =>
      api.put<BillingClient>(url, {
        pricing,
        price_per_credit: price.trim() || null,
        min_quantity: whole(least),
        max_quantity: whole(most),
        auto_approve_limit: limit.trim() || null,
        provider_cost_per_credit: cost.trim() || null,
        packages: packages
          .filter((p) => p.id.trim())
          .map((p) => ({ id: p.id.trim(), label: p.label.trim(), credits: Number(p.credits), price: p.price.trim() })),
      }),
    onSuccess: (data) => {
      onDone(data);
      onClose();
    },
    onError: (e) => toast.error('Not saved', refusalText(e)),
  });
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="SMS credit prices"
      description="Agreed with the client, before GST. A purchase is always priced by the server from what is saved here."
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Select
          label="How the client buys"
          options={[
            { value: 'fixed', label: 'Fixed price — any quantity at a price per credit' },
            { value: 'packages', label: 'Packages — only the packages below' },
          ]}
          value={pricing}
          onChange={(e) => setPricing(e.target.value as 'fixed' | 'packages')}
        />
        {pricing === 'fixed' ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Input label="Price a credit (₹)" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
            <Input label="Least at once" inputMode="numeric" value={least} onChange={(e) => setLeast(e.target.value)} />
            <Input label="Most at once" inputMode="numeric" value={most} onChange={(e) => setMost(e.target.value)} />
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Added at once up to (₹)"
            inputMode="decimal"
            value={limit}
            hint="When the vendor account has room. Empty: every purchase is looked at."
            onChange={(e) => setLimit(e.target.value)}
          />
          <Input
            label="Vendor's cost a credit (₹)"
            inputMode="decimal"
            value={cost}
            hint="What loading the vendor account costs per credit. Staff only."
            onChange={(e) => setCost(e.target.value)}
          />
        </div>
        {pricing === 'packages' ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted">Packages</p>
          {packages.map((pack, index) => (
            <div key={pack.id || `new-${index}`} className="grid grid-cols-4 gap-2">
              <Input size="sm" label="Id" value={pack.id} onChange={(e) => setPackages(packages.map((p, i) => (i === index ? { ...p, id: e.target.value } : p)))} />
              <Input size="sm" label="Label" value={pack.label} onChange={(e) => setPackages(packages.map((p, i) => (i === index ? { ...p, label: e.target.value } : p)))} />
              <Input size="sm" label="Credits" inputMode="numeric" value={pack.credits} onChange={(e) => setPackages(packages.map((p, i) => (i === index ? { ...p, credits: e.target.value } : p)))} />
              <Input size="sm" label="Price (₹)" inputMode="decimal" value={pack.price} onChange={(e) => setPackages(packages.map((p, i) => (i === index ? { ...p, price: e.target.value } : p)))} />
            </div>
          ))}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setPackages([...packages, { id: '', label: '', credits: '', price: '' }])}
          >
            <Plus className="size-3.5" />
            Add a package
          </Button>
        </div>
        ) : null}
        {save.error ? (
          <p role="alert" className="text-xs text-danger">
            {refusalText(save.error) ?? 'The prices could not be saved.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
